import { CSSProperties, RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Tooltip, TOOLTIP_SURFACE } from '@repo/atomic-components';
import { classNames as cn } from 'primereact/utils';
import { PLACEHOLDER_CLOSE, PLACEHOLDER_OPEN, PromptPlaceholder } from '../utils/promptPlaceholders';

/** How far a pill reaches past its text on each side. Matches `.prompt-placeholder` in index.css. */
const PILL_BLEED_PX = 6;

/** Text styles copied from the textarea, so the overlay's lines wrap at the same points. */
const MIRRORED_STYLES = [
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'fontVariant',
  'fontFeatureSettings',
  'lineHeight',
  'letterSpacing',
  'wordSpacing',
  'textTransform',
  'textIndent',
  'tabSize',
  'whiteSpace',
  'overflowWrap',
  'wordBreak',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'boxSizing',
] as const;

// PrimeReact sets the textarea ref in an effect, after this component's layout effects, so the
// overlay finds its textarea in the DOM.
const findTextarea = (overlay: HTMLElement | null) => overlay?.parentElement?.querySelector('textarea') ?? null;

interface PromptPlaceholderPillProps {
  text: string;
  interactive: boolean;
  onFill: () => void;
}

const PromptPlaceholderPill = ({ text, interactive, onFill }: PromptPlaceholderPillProps) => {
  const pillRef = useRef<HTMLSpanElement>(null);
  return (
    <>
      {/* The hidden brackets are part of the click target, so a click beside the pill fills it in too. */}
      <span
        ref={pillRef}
        data-testid="prompt-placeholder"
        className={cn(interactive && 'pointer-events-auto cursor-pointer')}
        onMouseDown={(event) => {
          if (!interactive || event.button !== 0) return;
          // Keeps the click from placing the caret; filling in puts it where the hint was.
          event.preventDefault();
          onFill();
        }}
      >
        <span className="text-transparent">{PLACEHOLDER_OPEN}</span>
        <span className="prompt-placeholder">{text}</span>
        <span className="text-transparent">{PLACEHOLDER_CLOSE}</span>
      </span>
      {interactive && (
        <Tooltip
          target={pillRef as RefObject<HTMLElement>}
          content="Click to fill in details"
          position="top"
          className={TOOLTIP_SURFACE}
          baseZIndex={10000}
        />
      )}
    </>
  );
};

interface PromptPlaceholderOverlayProps {
  value: string;
  placeholders: PromptPlaceholder[];
  /** Dims the text the way the disabled textarea is dimmed. */
  disabled: boolean;
  /** Pills take clicks only while the textarea can be edited. */
  interactive: boolean;
  /** The textarea's scroll fade. */
  style?: CSSProperties;
}

/**
 * Draws the prompt's text over the chat textarea, whose own text is transparent while this shows,
 * with each `[[[…]]]` placeholder as a pill and its brackets hidden. The textarea still handles the
 * caret, selection and typing. Clicking a pill deletes the placeholder and leaves the caret there.
 */
export const PromptPlaceholderOverlay = ({
  value,
  placeholders,
  disabled,
  interactive,
  style,
}: PromptPlaceholderOverlayProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  // The textarea's selection while it has focus. A placeholder with the caret inside shows as
  // plain text, brackets and all, so keyboard users can see what they're editing.
  const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);

  useLayoutEffect(() => {
    const overlay = overlayRef.current;
    const textarea = findTextarea(overlay);
    if (!overlay || !textarea) return;
    const computed = getComputedStyle(textarea);
    for (const property of MIRRORED_STYLES) overlay.style[property] = computed[property];
    // The overlay starts a little left of the textarea, so a pill at the start of a line isn't clipped.
    overlay.style.left = `-${PILL_BLEED_PX}px`;
    overlay.style.paddingLeft = `${parseFloat(computed.paddingLeft) + PILL_BLEED_PX}px`;
  }, []);

  // New text can move the textarea's scroll without a scroll event reaching us first.
  useLayoutEffect(() => {
    const overlay = overlayRef.current;
    const textarea = findTextarea(overlay);
    if (overlay && textarea) overlay.scrollTop = textarea.scrollTop;
  });

  useEffect(() => {
    const overlay = overlayRef.current;
    const textarea = findTextarea(overlay);
    if (!overlay || !textarea) return;

    const syncScroll = () => {
      overlay.scrollTop = textarea.scrollTop;
    };
    // A pill takes the wheel from the textarea under it, so scroll the textarea instead of the page.
    const forwardWheel = (event: WheelEvent) => {
      if (textarea.scrollHeight <= textarea.clientHeight) return;
      event.preventDefault();
      textarea.scrollTop += event.deltaY;
    };
    const trackSelection = () => {
      setSelection(
        document.activeElement === textarea ? { start: textarea.selectionStart, end: textarea.selectionEnd } : null,
      );
    };

    textarea.addEventListener('scroll', syncScroll);
    overlay.addEventListener('wheel', forwardWheel, { passive: false });
    textarea.addEventListener('focus', trackSelection);
    textarea.addEventListener('blur', trackSelection);
    document.addEventListener('selectionchange', trackSelection);
    return () => {
      textarea.removeEventListener('scroll', syncScroll);
      overlay.removeEventListener('wheel', forwardWheel);
      textarea.removeEventListener('focus', trackSelection);
      textarea.removeEventListener('blur', trackSelection);
      document.removeEventListener('selectionchange', trackSelection);
    };
  }, []);

  const fill = ({ start, end }: PromptPlaceholder) => {
    const textarea = findTextarea(overlayRef.current);
    if (!textarea) return;
    // Select before focusing: focusing first scrolls the textarea to its old caret. The pill was
    // clicked, so it's already on screen.
    textarea.setSelectionRange(start, end);
    textarea.focus({ preventScroll: true });
    // `delete` keeps the undo history and fires `input`.
    if (!document.execCommand?.('delete')) {
      textarea.setRangeText('', start, end, 'start');
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
    // Focusing recorded the placeholder as selected, and `selectionchange` only catches up a task
    // later, so the next placeholder would show as being edited for a frame.
    setSelection({ start, end: start });
  };

  const isBeingEdited = ({ start, end }: PromptPlaceholder) =>
    selection != null && [selection.start, selection.end].some((index) => index > start && index < end);

  const pills = placeholders.filter((placeholder) => !isBeingEdited(placeholder));

  return (
    <div
      ref={overlayRef}
      aria-hidden
      className={cn(
        'text-theme-black pointer-events-none absolute inset-y-0 right-0 overflow-hidden',
        disabled && 'opacity-60',
      )}
      style={style}
    >
      {pills.map((placeholder, index) => (
        // Keyed by position, so a filled-in pill unmounts with its tooltip instead of handing it on.
        <span key={placeholder.start}>
          {value.slice(pills[index - 1]?.end ?? 0, placeholder.start)}
          <PromptPlaceholderPill text={placeholder.text} interactive={interactive} onFill={() => fill(placeholder)} />
        </span>
      ))}
      {value.slice(pills.at(-1)?.end ?? 0)}
      {/* A trailing line break only takes up a line once something follows it. */}
      {value.endsWith('\n') && ' '}
    </div>
  );
};
